import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { WhatsappLogo } from "@phosphor-icons/react";
import { whatsappLink } from "@/data/site";
import { getSearchQuery, subscribeSearch } from "@/lib/searchStore";
import { useProducts, retryProducts } from "@/lib/productsStore";
import { useCategories } from "@/lib/categoriesStore";
import { ProductImg } from "@/components/ProductImg";

const SHARE_BASE = "https://rutadeportiva360.com/api/share";

const formatMXN = (n) => `$${n.toLocaleString("es-MX")} MXN`;

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const slugify = norm;

const isLocalImg = (src) => src?.startsWith("/images/") || src?.startsWith("/api/");

export const productWaMessage = (product) =>
  `Hola, me interesa ${product.name} (${formatMXN(product.price_mxn)}). ¿Está disponible?\n\n${SHARE_BASE}/${product.id}`;

const BADGE_STYLES = {
  "Más vendido": "bg-[#FF5508] text-white",
  Nuevo: "bg-black text-white",
  Oferta: "bg-[#DC2626] text-white",
};

export const CardBadges = ({ product }) => {
  const agotado = product.disponible === false;
  return (
    <>
      {agotado ? (
        <span
          data-testid={`agotado-${product.id}`}
          className="absolute top-3 left-3 z-10 text-[10px] font-semibold px-3 py-1.5 rounded-full bg-black/15 text-black/60"
        >
          Agotado
        </span>
      ) : (
        BADGE_STYLES[product.badge] && (
          <span
            data-testid={`badge-${product.id}`}
            className={`absolute top-3 left-3 z-10 text-[10px] font-semibold px-3 py-1.5 rounded-full ${BADGE_STYLES[product.badge]}`}
          >
            {product.badge}
          </span>
        )
      )}
      {!agotado && product.price_mxn >= 1399 && (
        <span
          data-testid={`envio-gratis-${product.id}`}
          className="absolute top-3 right-3 z-10 text-[10px] font-semibold bg-[#25D366] text-white px-3 py-1.5 rounded-full"
        >
          Envío gratis
        </span>
      )}
    </>
  );
};

const ProductCard = ({ product }) => {
  const navigate = useNavigate();

  return (
    <motion.article
      layout
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      onClick={() => navigate(`/producto/${product.id}`)}
      className="group bg-white border border-black/10 rounded-2xl overflow-hidden flex flex-col cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
    >
      <div className="relative bg-white aspect-square overflow-hidden">
        <CardBadges product={product} />
        <ProductImg
          src={product.image}
          alt={product.name}
          w={400}
          className={`w-full h-full object-contain p-5 transition-transform duration-300 ease-out group-hover:scale-105 ${product.disponible === false ? "opacity-60" : ""}`}
        />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-semibold text-lg leading-snug text-[#111418] transition-colors duration-300 group-hover:text-[#FF5508]">{product.name}</h3>
        <p data-testid={`product-price-${product.id}`} className="font-bold text-xl text-[#111418] mt-2">
          {formatMXN(product.price_mxn)}
        </p>
      </div>
    </motion.article>
  );
};

export const Products = () => {
  const { products, loaded, error } = useProducts();
  const { categories } = useCategories();
  const [category, setCategory] = useState("Todos");
  const [query, setQuery] = useState(getSearchQuery());
  const [searchParams] = useSearchParams();
  const catApplied = useRef(false);

  useEffect(() => subscribeSearch(setQuery), []);

  const catNames = categories.map((c) => c.nombre);

  useEffect(() => {
    if (catApplied.current) return;
    const cat = searchParams.get("cat");
    if (!cat) return;
    if (cat === "Todos" || catNames.includes(cat)) {
      catApplied.current = true;
      setCategory(cat);
      setTimeout(() => {
        document.getElementById("catalogo-lista")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, catNames.join("|")]);

  const byPrice = (a, b) => a.price_mxn - b.price_mxn;
  const grouped = catNames.map((cat) => ({
    cat,
    items: products.filter((p) => p.category === cat).sort(byPrice),
  })).filter((g) => g.items.length > 0);
  const filterCats = ["Todos", ...catNames.filter((n) => products.some((p) => p.category === n))];
  const filtered = products.filter((p) => p.category === category).sort(byPrice);

  const q = norm(query).trim();
  const searching = q.length > 0;
  const matchQuery = (p) =>
    [p.name, p.category, p.tagline, ...(p.specs || [])].some((f) => norm(f).includes(q));
  const searchBase = category === "Todos" ? products : filtered;
  const searchResults = searching ? searchBase.filter(matchQuery).sort(byPrice) : [];

  const pickCategory = (cat) => {
    setCategory(cat);
    document.getElementById("catalogo-lista")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="equipamiento" data-testid="products-section" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10"
        >
          <div>
            <p className="text-sm font-semibold text-[#FF5508] mb-4">Catálogo</p>
            <h2 className="font-display text-5xl sm:text-6xl uppercase font-semibold leading-none tracking-tight text-[#111418]">
              Equipamiento
              <br />
              <span className="text-outline">para tu aventura</span>
            </h2>
          </div>
          <p className="text-black/55 text-sm max-w-sm leading-relaxed">
            Precios en pesos mexicanos. Compra en línea o escríbenos por
            WhatsApp y te ayudamos a elegir.
          </p>
        </motion.div>

        {products.length > 0 && (
          <div data-testid="shop-by-category" className="mb-14">
            <h3 className="font-display text-3xl sm:text-4xl uppercase font-semibold tracking-tight text-[#111418] mb-6">
              Compra por categoría
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((cat) => {
                const items = products.filter((p) => p.category === cat.nombre).sort(byPrice);
                if (items.length === 0) return null;
                const first = items[0];
                const tileImg = cat.image || first.image;
                return (
                  <button
                    key={cat.nombre}
                    data-testid={`category-tile-${slugify(cat.nombre)}`}
                    onClick={() => pickCategory(cat.nombre)}
                    className="group/tile bg-white border border-black/10 rounded-2xl overflow-hidden text-left transition-all duration-300 hover:border-black hover:shadow-lg"
                  >
                    <div className="bg-white aspect-square overflow-hidden">
                      <ProductImg
                        src={tileImg}
                        alt={cat.nombre}
                        w={400}
                        className={`w-full h-full transition-transform duration-500 group-hover/tile:scale-105 ${
                          isLocalImg(tileImg) ? "object-contain p-4" : "object-cover"
                        }`}
                      />
                    </div>
                    <div className="px-4 py-3 border-t border-black/5">
                      <p className="font-semibold text-sm text-[#111418]">{cat.nombre}</p>
                      <p className="text-xs text-black/45 mt-0.5">
                        {items.length} {items.length === 1 ? "producto" : "productos"}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div
          id="catalogo-lista"
          data-testid="category-filters"
          className="flex gap-2 overflow-x-auto pb-2 mb-10 -mx-6 px-6 lg:mx-0 lg:px-0 scroll-mt-28"
        >
          {filterCats.map((c) => (
            <button
              key={c}
              data-testid={`filter-${slugify(c)}`}
              onClick={() => setCategory(c)}
              className={`shrink-0 text-sm font-semibold px-5 py-2.5 border rounded-full transition-colors duration-300 ${
                category === c
                  ? "bg-black text-white border-black"
                  : "bg-white text-black/60 border-black/15 hover:border-black"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {error && (
          <div data-testid="products-error" className="text-center py-10">
            <p className="text-black/50 text-sm mb-4">No pudimos cargar los productos.</p>
            <button
              data-testid="products-retry-button"
              onClick={retryProducts}
              className="bg-black text-white text-xs font-semibold px-6 py-3 rounded-full hover:bg-[#FF5508] transition-colors"
            >
              Reintentar
            </button>
          </div>
        )}

        {!error && !loaded && (
          <div data-testid="products-loading" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 animate-pulse">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white border border-black/10 rounded-2xl overflow-hidden">
                <div className="aspect-square bg-black/5" />
                <div className="p-5">
                  <div className="h-3 w-16 bg-black/10 rounded mb-3" />
                  <div className="h-4 w-3/4 bg-black/10 rounded mb-2" />
                  <div className="h-3 w-full bg-black/5 rounded mb-4" />
                  <div className="h-6 w-28 bg-black/10 rounded" />
                </div>
              </div>
            ))}
          </div>
        )}

        {searching ? (
          searchResults.length > 0 ? (
            <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <AnimatePresence mode="popLayout">
                {searchResults.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </AnimatePresence>
            </motion.div>
          ) : (
            <div data-testid="search-no-results" className="text-center py-16">
              <p className="text-black/60 text-base max-w-md mx-auto mb-6">
                No encontramos ese producto. Escríbenos por WhatsApp y te ayudamos a conseguirlo.
              </p>
              <a
                data-testid="search-whatsapp-button"
                href={whatsappLink(`Hola Ruta 360, busco: ${query}. ¿Me ayudan a conseguirlo?`)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#25D366] text-white text-sm font-semibold transition-colors duration-300 hover:bg-black"
              >
                <WhatsappLogo size={17} weight="fill" />
                WhatsApp
              </a>
            </div>
          )
        ) : category === "Todos" ? (
          grouped.map((g) => (
            <div key={g.cat} data-testid={`category-group-${slugify(g.cat)}`} className="mb-16 last:mb-0">
              <div className="flex items-end justify-between gap-4 mb-8">
                <h3 className="font-display text-4xl sm:text-5xl uppercase font-semibold tracking-tight text-[#111418]">
                  {g.cat}
                </h3>
                <button
                  data-testid={`ver-solo-${slugify(g.cat)}`}
                  onClick={() => setCategory(g.cat)}
                  className="text-sm font-semibold text-black/50 hover:text-[#FF5508] transition-colors duration-300 shrink-0"
                >
                  Ver solo {g.cat} →
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {g.items.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </div>
          ))
        ) : (
          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <AnimatePresence mode="popLayout">
              {filtered.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </section>
  );
};
